import {
	ACCEPTABLE_BODYSTYLES,
	BODYSTYLE_FACET_ID,
	STYLE_VARIANTS
} from '../global-constants';

/**
 * Where the shared Kinetic body style icons are hosted.
 */
const ICON_BASE_URL = 'https://static.dealer.com/assets/images/bodystyles';

/**
 * The inventory search a tile links to, faceted by the tile's body style.
 * Used only when the sitemap has no inventory listing URL to offer, so a
 * tile still points somewhere sensible.
 */
const DEFAULT_INVENTORY_PATH = '/new-inventory/index.htm';

/**
 * Style variation used when a caller names one that is not in
 * [STYLE_VARIANTS]. Matches the variant portion of the icon file names,
 * e.g. "Thick Outline" -> thick-outline.
 */
export const DEFAULT_STYLE_VARIANT = 'Thick Outline';

/**
 * Icon fill used when a caller names one that is not a hex color.
 */
export const DEFAULT_ICON_COLOR = '#000000';

/**
 * A three- or six-digit hex color, with the leading "#" optional so a
 * preference typed as "0072ce" is still usable.
 */
const HEX_COLOR = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalize = (value) => value.trim().toLowerCase();

/**
 * Splits a facet value's label into its individual body styles, lower-cased
 * for matching. Compound labels like "Van/Minivan" split into ["van", "minivan"].
 */
const toLabelParts = (label) =>
	typeof label === 'string' ? label.split('/').map(normalize) : [];

/**
 * Converts a display string into the lower-case, hyphen-separated slug used
 * in icon file names, e.g. "Cargo Van" -> "cargo-van" and
 * "Thick Outline" -> "thick-outline". Any run of non-alphanumeric characters
 * collapses into a single hyphen.
 *
 * @param {String} value The string to slugify.
 * @returns {String} The slug, or an empty string when nothing survives.
 */
const slugify = (value) =>
	String(value ?? '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

/**
 * Pull the body style facet's values out of a getFacets() payload.
 *
 * @param {Object} payload The object returned by InvData.getFacets().
 * @returns {Array | null} The facet's values, or null when the facet is absent.
 */
export const readFacetValues = (payload) =>
	payload?.facets?.find(({ id }) => id === BODYSTYLE_FACET_ID)?.values ??
	null;

/**
 * Reduce facet values to the acceptable body styles they represent, pairing
 * the label to display with the value the inventory search filters on. The
 * two differ: a facet value labelled "Van/Minivan" is displayed as "Van".
 *
 * Walking ACCEPTABLE_BODYSTYLES gives the result that list's canonical
 * spelling and alphabetical order and keeps it free of duplicates, while
 * values stay as the endpoint reported them. Labels are matched case- and
 * whitespace-insensitively, and the first facet value to match one wins.
 *
 * @param {Array} values The facet's values.
 * @returns {Array<{ label: String, value: String }>} The matching body styles.
 */
export const toAcceptableBodystyles = (values) =>
	ACCEPTABLE_BODYSTYLES.flatMap((label) => {
		const match = (values ?? []).find((facetValue) =>
			toLabelParts(facetValue.label).includes(normalize(label))
		);

		return match
			? [{ label, value: String(match.value ?? match.label).trim() }]
			: [];
	});

/**
 * Narrow a style variation to one of [STYLE_VARIANTS]. The `styleVariant`
 * preference is free to hold anything a stale or hand-edited site config
 * carries, and a variant with no icon behind it would render a broken image,
 * so anything unrecognized falls back to [DEFAULT_STYLE_VARIANT]. Matching is
 * done on the slug, so it ignores case and separator differences, and the
 * result takes the list's spelling.
 *
 * @param {String} [styleVariant] The requested style variation.
 * @returns {String} A variant from [STYLE_VARIANTS].
 */
export const resolveStyleVariant = (styleVariant) => {
	const slug = slugify(styleVariant);

	return (
		STYLE_VARIANTS.find((variant) => slugify(variant) === slug) ??
		DEFAULT_STYLE_VARIANT
	);
};

/**
 * Narrow an icon fill to a usable hex color, falling back to
 * [DEFAULT_ICON_COLOR] for anything else. The `graphicsColor` preference is
 * free text, so it can hold a typo, a color name or nothing at all. A valid
 * value is returned with its "#" in place.
 *
 * @param {String} [iconColor] The requested icon fill.
 * @returns {String} A hex color.
 */
export const resolveIconColor = (iconColor) => {
	const value = typeof iconColor === 'string' ? iconColor.trim() : '';

	return HEX_COLOR.test(value)
		? `#${value.replace('#', '')}`
		: DEFAULT_ICON_COLOR;
};

/**
 * Build the URL of a body style's icon.
 *
 * @param {String} bodystyleName A body style label, e.g. "Cargo Van".
 * @param {String} [styleVariant] A style variation, e.g. "Thick Outline".
 * @returns {String} The icon URL, or an empty string without a body style.
 */
export const buildBodyStyleImgUrl = (bodystyleName, styleVariant) => {
	const name = slugify(bodystyleName);

	if (!name) {
		return '';
	}

	const slug = [name, slugify(resolveStyleVariant(styleVariant))].join('-');

	return `${ICON_BASE_URL}/${slug}.svg`;
};

/**
 * Turn body styles into the tile data wsm-tiles renders for
 * `type="bodystyle"`, keeping their incoming order.
 *
 * @param {Array<{ label: String, value: String }>} bodystyles The body styles
 * to build tiles for. `value` falls back to `label` when it is missing.
 * @param {Object} [options]
 * @param {String} [options.styleVariant] The style variation to request. Falls
 * back to [DEFAULT_STYLE_VARIANT] unless it names one of [STYLE_VARIANTS].
 * @param {String} [options.iconColor] The icon fill, as a hex color. Falls
 * back to [DEFAULT_ICON_COLOR] unless it is one.
 * @param {String} [options.inventoryPath] The inventory search the tiles link
 * to, normally the sitemap's inventory listing URL. Falls back to
 * [DEFAULT_INVENTORY_PATH] when missing or empty, which is what a site
 * without that sitemap alias reports.
 * @returns {Array<{ bodystyle: Object }>} One tile datum per body style.
 */
export const generateTileData = (
	bodystyles,
	{ styleVariant, iconColor, inventoryPath } = {}
) =>
	(bodystyles ?? []).map(({ label, value }) => ({
		bodystyle: {
			styleName: label,
			bodyStyleImgUrl: buildBodyStyleImgUrl(label, styleVariant),
			href: `${
				inventoryPath || DEFAULT_INVENTORY_PATH
			}?${BODYSTYLE_FACET_ID}=${encodeURIComponent(value ?? label)}`,
			iconColor: resolveIconColor(iconColor)
		}
	}));
